<section class="rounded-2xl border border-border bg-card shadow-sm">
  <header
    class="sticky top-11 z-20 flex cursor-pointer items-center gap-2.5 rounded-2xl bg-card p-5"
    [class.shadow-sm]="!collapsed()"
    (click)="toggle()"
  >
    <span class="grid h-8 w-8 place-items-center rounded-lg bg-primary/10 text-primary">
      <hlm-icon name="lucideCoins" size="1rem" />
    </span>
    <h2 class="font-display text-lg font-semibold text-foreground">Usage Credit</h2>
    @if (!loading()) {
      <hlm-badge variant="secondary" class="ml-1 text-sm">{{ sortedCredits().length }}</hlm-badge>
    }

    <button
      hlmBtn
      variant="default"
      size="sm"
      class="ml-auto"
      (click)="openAddDialog(); $event.stopPropagation()"
    >
      <hlm-icon name="lucidePlus" size="0.9rem" />
      New Entry
    </button>
    <hlm-icon
      [name]="collapsed() ? 'lucideChevronDown' : 'lucideChevronUp'"
      size="1rem"
      class="text-muted-foreground"
    />
  </header>

  @if (!collapsed()) {
    <div class="flex flex-col gap-4 p-5 pt-4" (click)="$event.stopPropagation()">
      @if (loading()) {
        <div class="flex items-center gap-3 p-6 text-sm text-muted-foreground">
          <hlm-spinner size="20" />
          Loading credits…
        </div>
      } @else if (sortedCredits().length === 0) {
        <div
          class="rounded-2xl border border-dashed border-border bg-card/50 p-12 text-center text-sm text-muted-foreground"
        >
          No credits found
        </div>
      } @else {
        <div class="overflow-hidden rounded-xl border border-border">
          <!-- Cap to 8 visible rows; beyond that → scroll + one-time "Show all" (EB-3856 capped-list pattern) -->
          <cmt-capped-list [count]="sortedCredits().length" [threshold]="8" [maxHeightRem]="21">
            <table class="w-full text-sm">
              <thead class="sticky top-0 z-10 bg-muted/80 backdrop-blur">
                <tr class="cmt-eyebrow">
                  <th class="px-3 py-2 text-left">Type</th>
                  <th class="px-3 py-2 text-right">Amount</th>
                  <th class="px-3 py-2 text-left">Inserted On</th>
                  <th class="px-3 py-2 text-left">Valid Until</th>
                  <th class="px-3 py-2 text-left">Description</th>
                  <th class="px-3 py-2 text-left">Invoice</th>
                </tr>
              </thead>
              <tbody>
                @for (credit of sortedCredits(); track credit.id) {
                  <tr class="border-t border-border text-foreground transition-colors hover:bg-muted/60">
                    <td class="px-3 py-2">
                      @if (typeBadgeVariant(credit.type); as variant) {
                        <hlm-badge [variant]="variant">
                          {{ creditTypeLabel(credit.type) }}
                        </hlm-badge>
                      } @else {
                        <span class="font-mono-feat text-muted-foreground">{{ credit.type }}</span>
                      }
                    </td>
                    <td class="px-3 py-2 text-right font-mono-feat">
                      {{ credit.amount | number:'1.2-2' }}
                    </td>
                    <td class="px-3 py-2 font-mono-feat">
                      {{ credit.insertedOn | date:'dd.MM.yyyy' }}
                    </td>
                    <td class="px-3 py-2 font-mono-feat">
                      @if (credit.validUntil) {
                        {{ credit.validUntil | date:'dd.MM.yyyy' }}
                      } @else {
                        <span class="text-muted-foreground/60">—</span>
                      }
                    </td>
                    <td class="max-w-[240px] px-3 py-2 text-muted-foreground">
                      <span class="block truncate transition-colors hover:text-foreground" [title]="credit.reason ?? ''">
                        {{ credit.reason || '—' }}
                      </span>
                    </td>
                    <td class="px-3 py-2 font-mono-feat">
                      <div class="flex items-center justify-between gap-2">
                        @if (credit.invoiceId !== null) {
                          @let officialId = officialInvoiceNumber(credit.invoiceId);
                          <span class="inline-flex items-center gap-1.5">
                            @if (officialId !== null) {
                              <span class="text-foreground">#{{ officialId }}</span>
                              <button
                                type="button"
                                [title]="'Go to invoice ' + officialId"
                                (click)="jumpToInvoice.emit(credit.invoiceId!); $event.stopPropagation()"
                                class="inline-flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-primary/10 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
                                aria-label="Go to invoice"
                              >
                                <hlm-icon name="lucideArrowUpRight" size="0.8rem" />
                              </button>
                            } @else {
                              <span class="text-muted-foreground">internal {{ credit.invoiceId }}</span>
                            }
                          </span>
                        } @else {
                          <span class="text-muted-foreground/60">—</span>
                        }
                        @if (isDeletable(credit)) {
                          <button
                            hlmBtn
                            variant="ghost"
                            size="icon"
                            class="h-9 w-9 text-muted-foreground/70 hover:bg-destructive hover:text-destructive-foreground"
                            title="Delete this credit"
                            aria-label="Delete this credit"
                            (click)="deleteCredit(credit); $event.stopPropagation()"
                          >
                            <hlm-icon name="lucideTrash2" size="1.1rem" />
                          </button>
                        }
                      </div>
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </cmt-capped-list>

          <div
            class="flex flex-wrap items-center justify-between gap-4 border-t border-border bg-muted/30 px-3 py-2 text-sm text-muted-foreground"
          >
            <span class="font-mono-feat">
              {{ sortedCredits().length }} {{ sortedCredits().length === 1 ? 'credit' : 'credits' }}
            </span>
          </div>
        </div>
      }
    </div>
  }
</section>
